/**
 * Known fairground/venue coordinates for Google Maps anchoring.
 * Keys: lowercase venue_city from event_metadata.
 * The MDS-derived attraction positions get scaled onto these real-world bounds.
 */

export interface VenueCoords {
  lat: number
  lng: number
  /** Approximate radius of the midway area in meters */
  radiusM: number
  /** Human-readable venue name */
  venue: string
}

export const VENUE_COORDINATES: Record<string, VenueCoords> = {
  'grass valley': { lat: 39.2191, lng: -121.0608, radiusM: 200, venue: 'Nevada County Fairgrounds' },
  'indio': { lat: 33.7207, lng: -116.2156, radiusM: 250, venue: 'Riverside County Fairgrounds' },
  'san bernardino': { lat: 34.1011, lng: -117.2767, radiusM: 300, venue: 'National Orange Show Events Center' },
  'fresno': { lat: 36.7327, lng: -119.8084, radiusM: 350, venue: 'Fresno Fairgrounds' },
  'bakersfield': { lat: 35.3856, lng: -119.0128, radiusM: 300, venue: 'Kern County Fairgrounds' },
  'santa rosa': { lat: 38.4364, lng: -122.7383, radiusM: 300, venue: 'Sonoma County Fairgrounds' },
  'auburn': { lat: 38.9022, lng: -121.0769, radiusM: 200, venue: 'Gold Country Fairgrounds' },
  'lodi': { lat: 38.1254, lng: -121.2694, radiusM: 200, venue: 'Lodi Grape Festival Grounds' },
  'bishop': { lat: 37.3635, lng: -118.3942, radiusM: 150, venue: 'Tri County Fairgrounds' },
  'ridgecrest': { lat: 35.6225, lng: -117.6709, radiusM: 200, venue: 'Desert Empire Fairgrounds' },
  'blackfoot': { lat: 43.1907, lng: -112.3453, radiusM: 350, venue: 'Eastern Idaho State Fairgrounds' },
  'boise': { lat: 43.6024, lng: -116.2291, radiusM: 350, venue: 'Expo Idaho' },
  'mesa': { lat: 33.4314, lng: -111.8226, radiusM: 200, venue: 'Mesa Riverview' },
  'chandler': { lat: 33.2463, lng: -111.8603, radiusM: 250, venue: 'Rawhide Western Town' },
  'maricopa': { lat: 33.0581, lng: -112.0476, radiusM: 200, venue: 'Maricopa Fairgrounds' },
  'costa mesa': { lat: 33.6691, lng: -117.9119, radiusM: 400, venue: 'OC Fair & Event Center' },
}

/**
 * Convert MDS pixel coordinates (0-600 range) to lat/lng centered on a venue.
 * Uses equirectangular approximation — accurate enough at fairground scale (~500m).
 */
export function mdsToLatLng(
  x: number, y: number,
  center: VenueCoords,
  mdsSize: number = 600,
): { lat: number; lng: number } {
  const halfMds = mdsSize / 2
  const normalizedX = (x - halfMds) / halfMds // -1 to 1
  const normalizedY = (y - halfMds) / halfMds

  const metersPerDegreeLat = 111320
  const metersPerDegreeLng = 111320 * Math.cos(center.lat * Math.PI / 180)

  const offsetLat = -(normalizedY * center.radiusM) / metersPerDegreeLat
  const offsetLng = (normalizedX * center.radiusM) / metersPerDegreeLng

  return {
    lat: center.lat + offsetLat,
    lng: center.lng + offsetLng,
  }
}

/**
 * Affine transform coefficients solved from 3 control point pairs.
 * Maps MDS (x,y) → GPS (lat,lng) via:
 *   lat = a*x + b*y + c
 *   lng = d*x + e*y + f
 */
export interface AffineTransform {
  a: number; b: number; c: number
  d: number; e: number; f: number
}

/**
 * Solve a 3-point affine transform from MDS coordinates to GPS coordinates.
 * Given 3 pairs of (mdsX, mdsY) → (lat, lng), computes the 6 affine
 * coefficients using Cramer's rule.
 */
export function solveAffineTransform(
  mds: [{ x: number; y: number }, { x: number; y: number }, { x: number; y: number }],
  gps: [{ lat: number; lng: number }, { lat: number; lng: number }, { lat: number; lng: number }],
): AffineTransform | null {
  const [m0, m1, m2] = mds
  const [g0, g1, g2] = gps

  // System of equations for lat: a*x + b*y + c = lat
  //   a*x0 + b*y0 + c = lat0
  //   a*x1 + b*y1 + c = lat1
  //   a*x2 + b*y2 + c = lat2
  const det =
    m0.x * (m1.y - m2.y) -
    m0.y * (m1.x - m2.x) +
    (m1.x * m2.y - m2.x * m1.y)

  if (Math.abs(det) < 1e-12) return null

  const a = (g0.lat * (m1.y - m2.y) - m0.y * (g1.lat - g2.lat) + (g1.lat * m2.y - g2.lat * m1.y)) / det
  const b = (m0.x * (g1.lat - g2.lat) - g0.lat * (m1.x - m2.x) + (m1.x * g2.lat - m2.x * g1.lat)) / det
  const c = (m0.x * (m1.y * g2.lat - m2.y * g1.lat) - m0.y * (m1.x * g2.lat - m2.x * g1.lat) + g0.lat * (m1.x * m2.y - m2.x * m1.y)) / det

  const d = (g0.lng * (m1.y - m2.y) - m0.y * (g1.lng - g2.lng) + (g1.lng * m2.y - g2.lng * m1.y)) / det
  const e = (m0.x * (g1.lng - g2.lng) - g0.lng * (m1.x - m2.x) + (m1.x * g2.lng - m2.x * g1.lng)) / det
  const f = (m0.x * (m1.y * g2.lng - m2.y * g1.lng) - m0.y * (m1.x * g2.lng - m2.x * g1.lng) + g0.lng * (m1.x * m2.y - m2.x * m1.y)) / det

  return { a, b, c, d, e, f }
}

/**
 * Apply an affine transform to convert MDS coordinates to GPS.
 */
export function affineToLatLng(
  x: number, y: number,
  transform: AffineTransform,
): { lat: number; lng: number } {
  return {
    lat: transform.a * x + transform.b * y + transform.c,
    lng: transform.d * x + transform.e * y + transform.f,
  }
}
